import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputComponent } from './input/input.component';

export interface IResponsiveColumnsMap {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

const BREAKPOINT_ALIASES: Array<[keyof IResponsiveColumnsMap, string]> = [
  ['xs', Breakpoints.XSmall],
  ['sm', Breakpoints.Small],
  ['md', Breakpoints.Medium],
  ['lg', Breakpoints.Large],
  ['xl', Breakpoints.XLarge],
];

// Usage: <odq-input [responsiveColPercent]="{xs: 2, sm: 2, md: 4, lg: 6, xl: 8}">
@Directive({
    selector: '[responsiveColPercent]',
    standalone: false
})
export class ResponsiveColPercentDirective implements OnInit, OnDestroy {
  private countBySize: IResponsiveColumnsMap = { xs: 2, sm: 2, md: 4, lg: 6, xl: 8 };

  destroy$ = new Subject<void>();

  public get cols(): IResponsiveColumnsMap {
    return this.countBySize;
  }

  @Input('responsiveColPercent')
  public set cols(map: IResponsiveColumnsMap) {
    if (map && (typeof map === 'object')) {
      this.countBySize = map;
    }
  }

  public constructor(
    private inputGroup: InputComponent,
    private breakpointObserver: BreakpointObserver,
  ) {}

  public ngOnInit(): void {
    this.applyColPercentForCurrentBreakpoint();

    this.breakpointObserver
      .observe(BREAKPOINT_ALIASES.map(([, query]) => query))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyColPercentForCurrentBreakpoint());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyColPercentForCurrentBreakpoint(): void {
    for (const [alias, query] of BREAKPOINT_ALIASES) {
      if (this.breakpointObserver.isMatched(query) && this.countBySize[alias] !== undefined) {
        this.inputGroup.colPercentSize = this.countBySize[alias];
        return;
      }
    }
  }
}
